import { s3PublicUrl } from "./s3PublicUrl";

/**
 * Turns raw theme settings (themesdata / published snapshot) into everything the
 * Crossroads screens render, with a default for every field so a half-configured theme
 * still plays. Defaults mirror backend/themes/themesets.js.
 */

const DEFAULTS = {
  game_title: "Crossroads",
  primary_color: "#2E86DE",
  secondary_color: "#1B2A49",
  text_color: "#1B2A49",
  button_background_color: "#F39C12",
  button_text_color: "#ffffff",
  popup_color: "#ffffff",
  popup_text_color: "#1B2A49",
  path_a_color: "#1F6FFF",
  path_b_color: "#FF5A1F",
  intro_title: "Your journey begins",
  intro_text:
    "Every journey is filled with choices. At every crossroads, your decision will shape what happens next. Choose wisely and complete your journey by making the best possible decisions.",
  dyk_default_heading: "Did You Know?",
  completion_title: "Journey Complete!",
  completion_message: "You've reached your destination. Here's how your decisions shaped the journey.",
};

const DEFAULT_RULES = [
  "Read the scenario at each crossroads.",
  "Choose one of the two paths.",
  "See the outcome and learn from your choice.",
  "Continue the journey till you reach the final destination.",
];
const DEFAULT_SUCCESS = ["Great choice!", "Spot on — that's the right road.", "Well navigated!"];
const DEFAULT_INCORRECT = [
  "Not quite — that road had a bump.",
  "That wasn't the best path this time.",
  "Hmm, a detour. Let's learn from it.",
];

// Distinct, saturated pairs that read well on a road and against each other.
const RANDOM_PATH_PALETTE = [
  "#2E86DE",
  "#E67E22",
  "#27AE60",
  "#8E44AD",
  "#E74C3C",
  "#16A085",
  "#D4AC0D",
  "#C0392B",
  "#2C3E50",
  "#D35400",
];

const HEX_RE = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

function str(v, fallback = "") {
  const s = v == null ? "" : String(v).trim();
  return s || fallback;
}

function color(v, fallback) {
  const s = str(v);
  return HEX_RE.test(s) ? s : fallback;
}

function bool(v, fallback) {
  if (v === true || v === "true") return true;
  if (v === false || v === "false") return false;
  return fallback;
}

function list(v, fallback) {
  let arr = v;
  if (typeof arr === "string") {
    try {
      arr = JSON.parse(arr);
    } catch {
      arr = arr.split("\n");
    }
  }
  if (!Array.isArray(arr)) return fallback;
  const clean = arr.map((x) => str(x)).filter(Boolean);
  return clean.length ? clean : fallback;
}

function image(v) {
  const s = str(v);
  return s ? s3PublicUrl(s) : "";
}

export function resolveCrossroadsTheme(settings) {
  const s = settings || {};
  const votingSeconds = Math.floor(Number(s.voting_duration_seconds));
  return {
    title: str(s.game_title, str(s.newthemeName, DEFAULTS.game_title)),
    mode: s.game_mode === "live" ? "live" : "self_paced",
    shuffle: bool(s.shuffle_questions, false),
    showCompletionTime: bool(s.show_completion_time, true),
    // Off = the leaderboard page shows the player's own score + the admin's message instead.
    leaderboardEnabled: bool(s.leaderboard_enabled, true),
    leaderboardMessage: str(s.leaderboard_message, ""),
    // Off = every crossroads is choice based (no right road, no points).
    hasCorrectAnswers: bool(s.has_correct_answers, true),
    images: {
      logo: image(s.logo),
      journeyDesktop: image(s.background_desk),
      journeyMobile: image(s.background_mob) || image(s.background_desk),
      // Portrait board art only — the desktop image's layout doesn't match the portrait roads.
      journeyPortrait: image(s.background_mob),
      popup: image(s.popup_bg), // optional popup background (older themes)
    },
    colors: {
      primary: color(s.primary_color, DEFAULTS.primary_color),
      secondary: color(s.secondary_color, DEFAULTS.secondary_color),
      text: color(s.text_color, DEFAULTS.text_color),
      button: color(s.button_background_color, color(s.box_bg_color, DEFAULTS.button_background_color)),
      buttonText: color(s.button_text_color, DEFAULTS.button_text_color),
      popup: color(s.popup_color, DEFAULTS.popup_color),
      popupText: color(s.popup_text_color, DEFAULTS.popup_text_color),
      pathA: color(s.path_a_color, DEFAULTS.path_a_color),
      pathB: color(s.path_b_color, DEFAULTS.path_b_color),
    },
    introTitle: str(s.intro_title, DEFAULTS.intro_title),
    introText: str(s.intro_text, DEFAULTS.intro_text),
    rules: list(s.rules, DEFAULT_RULES),
    successMessages: list(s.success_messages, DEFAULT_SUCCESS),
    incorrectMessages: list(s.incorrect_messages, DEFAULT_INCORRECT),
    dykHeading: str(s.dyk_default_heading, DEFAULTS.dyk_default_heading),
    completionTitle: str(s.completion_title, DEFAULTS.completion_title),
    completionMessage: str(s.completion_message, DEFAULTS.completion_message),
    votingSeconds: Number.isFinite(votingSeconds) && votingSeconds >= 5 ? Math.min(votingSeconds, 600) : 30,
  };
}

/** Small deterministic hash so every device derives the same "random" choices. */
export function seededNumber(seed) {
  const s = String(seed ?? "");
  let h = 2166136261;
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/**
 * Path colours for one crossroads. Random mode is seeded by the crossroads' content (scenario +
 * both options), not its database id: the host screen and every phone show the same two colours
 * (PRD 7.2), and so does every session's copy of the same crossroads (copies get new ids).
 */
export function pathColorsFor(question, theme) {
  const mode = String(question?.path_color_mode || "theme").toLowerCase();
  if (mode === "custom") {
    return {
      a: color(question.path_a_color, theme.colors.pathA),
      b: color(question.path_b_color, theme.colors.pathB),
    };
  }
  if (mode === "random") {
    const n = RANDOM_PATH_PALETTE.length;
    const key = [question?.question_name, question?.option_one, question?.option_two].map((s) => String(s ?? "").trim()).join("|");
    const first = seededNumber(`path-${key}`) % n;
    const offset = 1 + (seededNumber(`path-b-${key}`) % (n - 1));
    return { a: RANDOM_PATH_PALETTE[first], b: RANDOM_PATH_PALETTE[(first + offset) % n] };
  }
  return { a: theme.colors.pathA, b: theme.colors.pathB };
}

/** Black or white text, whichever reads better on `hex`. */
export function readableTextOn(hex) {
  const m = String(hex || "").trim().match(/^#([0-9a-f]{6}|[0-9a-f]{3})$/i);
  if (!m) return "#ffffff";
  let h = m[1];
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16) / 255);
  const lin = (c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  const L = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  return L > 0.45 ? "#1B2A49" : "#ffffff";
}

/** Choice-based crossroads have no right answer and give no points. */
export function isChoiceQuestion(question) {
  return question?.question_type === "choice";
}

/** The correct road ("A" / "B"), or null for a choice-based crossroads. */
export function correctLetter(question) {
  if (isChoiceQuestion(question)) return null;
  return String(question?.answer_id) === "2" ? "B" : "A";
}

/**
 * What a crossroads shows once a road is taken — same shape the backend's saveResponses
 * returns to players. Built locally only where the full content is available (admin preview,
 * live host).
 */
export function revealFor(question) {
  return {
    correct: correctLetter(question),
    dyk_heading: question?.dyk_heading || "",
    more_info: question?.more_info || "",
  };
}

export function pickMessage(messages, seed) {
  if (!messages?.length) return "";
  return messages[seededNumber(seed) % messages.length];
}

/** Fisher–Yates with a seed, so a refresh mid-journey keeps the same order. */
export function seededShuffle(items, seed) {
  const out = [...items];
  let state = seededNumber(seed) || 1;
  const rand = () => {
    state ^= state << 13;
    state ^= state >>> 17;
    state ^= state << 5;
    return ((state >>> 0) % 100000) / 100000;
  };
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rand() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

export function formatDuration(totalSeconds) {
  const s = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
}

/** Theme colours as CSS custom properties for a page root. */
export function themeCssVars(theme) {
  const c = theme.colors;
  return {
    "--cr-primary": c.primary,
    "--cr-secondary": c.secondary,
    "--cr-text": c.text,
    "--cr-button": c.button,
    "--cr-button-text": c.buttonText,
    "--cr-popup": c.popup,
    "--cr-popup-text": c.popupText,
  };
}
